import {
  passwordExpiredChangePassword,
  passwordExpiredGenerateOTP,
  forgotPasswordGenerateOTP,
  changePasswordVerifyOTP,
  forgotPasswordVerifyOTP,
  changePasswordGenerateOTP,
  USER_LOGOUT,
} from "@/service/login.service";
import React, { useEffect, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import OtpInput from "./OtpInput";
import { toast } from "react-toastify";
import { handleAxiosError } from "@/lib/errorHandler";

/**
 * Modal component to handle password expiration by verifying OTP and setting a new password.
 *
 * @component
 * @param {Object} props - React props
 * @param {boolean} props.isOpen - Flag to control the modal visibility
 * @param {Function} props.onClose - Function to close the modal
 * @param {string} props.userName - Email or username associated with the account
 * @param {string} props.password - Current (expired) password
 * @returns {JSX.Element|null}
 */
function PasswordExpiredModal({ isOpen, onClose, userName, password, type }) {
  const [otp, setOtp] = useState(Array(6).fill(""));
  const [newPassword, setNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [hasGeneratedOTP, setHasGeneratedOTP] = useState(false);
  const [timer, setTimer] = useState(0); // Timer in seconds (starts at 0)
  const [isTimerActive, setIsTimerActive] = useState(false); // Flag to track if timer is active (starts false)

  /**
   * Handles input change for OTP fields.
   *
   * @param {string} value - The digit entered
   * @param {number} index - Index of the current OTP input
   */
  const handleInputChange = (value, index) => {
    if (/^\d?$/.test(value)) {
      setOtp((prevOtp) => {
        const updatedOtp = [...prevOtp];
        updatedOtp[index] = value;
        return updatedOtp;
      });

      if (value && index <= 5) {
        const nextInput = document.getElementById(`otp-input-${index + 1}`);
        if (nextInput) nextInput.focus();
      }
    }
  };

  /**
   * Handles backspace key press in OTP inputs.
   *
   * @param {number} index - Index of the OTP input field
   */
  const handleBackspace = (index) => {
    setOtp((prevOtp) => {
      const updatedOtp = [...prevOtp];
      if (updatedOtp[index] === "" && index > 0) {
        const prevInput = document.getElementById(`otp-input-${index - 1}`);
        if (prevInput) prevInput.focus();
      }
      updatedOtp[index] = "";
      return updatedOtp;
    });
  };

  /**
   * Handles form submission to verify OTP and change password.
   *
   * @param {React.FormEvent<HTMLFormElement>} e - Form submit event
   */
  const handleSubmit = (e) => {
    e.preventDefault();
    const otpCode = otp.join("");
    if (otpCode.length !== 6) {
      handleAxiosError(null,{"customMessage": "Please enter a valid 6-digit OTP."});
      return;
    }
    changePassword(otpCode);
  };

  /**
   * Starts the 3-minute timer and disables resend button
   */
  const startTimer = () => {
    setTimer(180); // Set timer to 3 minutes (180 seconds)
    setIsTimerActive(true);
  };

  /**
   * Calls API to generate OTP for password reset.
   */
  const generateOTP = async () => {
    try {
      if (type === "expired") {
        await passwordExpiredGenerateOTP({
          userName,
          password,
        });
        toast.success("OTP sent successfully!");
      } else if (type === "change") {
        await changePasswordGenerateOTP({ userName, password });
        toast.success("OTP generated successfully");
      } else if (type === "forgot") {
        await forgotPasswordGenerateOTP({ userName });
        toast.success("OTP generated successfully");
      }
      // Start timer after successful OTP generation for all types
      startTimer();
    } catch (error) {
      handleAxiosError(error);
    }
  };

  /**
   * Calls API to update the expired password using OTP and new credentials.
   *
   * @param {string} otp - 6-digit OTP code
   */
  const changePassword = async (otp) => {
    try {
      if (type === "expired") {
        await passwordExpiredChangePassword({
          userName,
          newPassword,
          confirmPassword,
          otp,
        });
      } else if (type === "forgot") {
        await forgotPasswordVerifyOTP({
          userName,
          newPassword,
          confirmPassword,
          otp,
        });
      } else if (type === "change") {
        await changePasswordVerifyOTP({
          userName,
          newPassword,
          confirmPassword,
          otp,
        });
        const refreshToken = sessionStorage.getItem("refresh_token");
        if (refreshToken) {
          await USER_LOGOUT(refreshToken);
          sessionStorage.clear();
          localStorage.clear();
        }

        // Redirect to login page
        window.location.href = "/";
      }
      toast.success("Password changed successfully!");
      onClose();
    } catch (error) {
      handleAxiosError(error);
    }
  };

  /**
   * Re-sends OTP, shows a toast notification, and starts the timer.
   */
  const handleResend = async () => {
    try {
      if (type === "expired") {
        await passwordExpiredGenerateOTP({
          userName,
          password,
        });
      } else if (type === "change") {
        await changePasswordGenerateOTP({ userName, password });
      } else if (type === "forgot") {
        await forgotPasswordGenerateOTP({ userName });
      }
      toast.success("OTP resent successfully!");
      startTimer();
      setOtp(Array(6).fill(""));
    } catch (error) {
      handleAxiosError(error);
    }
  };

  /** Toggles visibility of the new password field */
  const handleToggleNewPassword = () => setShowNewPassword((prev) => !prev);

  /** Toggles visibility of the confirm password field */
  const handleToggleConfirmPassword = () =>
    setShowConfirmPassword((prev) => !prev);

  // Timer countdown effect
  useEffect(() => {
    let countdown;
    if (isTimerActive && timer > 0) {
      countdown = setInterval(() => {
        setTimer((prevTimer) => {
          if (prevTimer <= 1) {
            setIsTimerActive(false);
            return 0;
          }
          return prevTimer - 1;
        });
      }, 1000);
    }
    return () => {
      if (countdown) {
        clearInterval(countdown);
      }
    };
  }, [isTimerActive, timer]);

  /**
   * On mount: generates OTP and resets states on unmount.
   */
  useEffect(() => {
    if (isOpen && !hasGeneratedOTP) {
      if (type === "change" || type === "forgot") {
        startTimer();
      } else {
        generateOTP();
      }
      setHasGeneratedOTP(true);
    }

    // Reset state when modal closes
    if (!isOpen) {
      setHasGeneratedOTP(false);
      setNewPassword("");
      setShowNewPassword(false);
      setConfirmPassword("");
      setShowConfirmPassword(false);
      setTimer(0);
      setIsTimerActive(false);
      setOtp(Array(6).fill(""));
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      // Reset state on unmount
      setNewPassword("");
      setShowNewPassword(false);
      setConfirmPassword("");
      setShowConfirmPassword(false);
      setHasGeneratedOTP(false);
      setTimer(0);
      setIsTimerActive(false);
      setOtp(Array(6).fill(""));
    };
  }, []);

  // Validation rules for new password
  const validateNewPassword = {
    lowercase: /[a-z]/.test(newPassword),
    uppercase: /[A-Z]/.test(newPassword),
    number: /\d/.test(newPassword),
    minLength: newPassword.length >= 8,
    maxLength: newPassword.length >= 8 && newPassword.length <= 15,
    consecutive: newPassword && !/(.)\1{1,}/.test(newPassword),
    specialChar: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword),
  };
  //Validation rules for confirm password
  const validateNewConfirmPassword = {
    isSame:
      newPassword.length !== 0 &&
      confirmPassword.length !== 0 &&
      newPassword === confirmPassword,
  };

  const isValidNewPassword = Object.values(validateNewPassword).every(Boolean);
  const isValidConfirmNewPassword = validateNewConfirmPassword.isSame;

  // Format timer in MM:SS
  const formatTimer = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
      .toString()
      .padStart(2, "0")}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl w-[40%] max-h-[90vh] overflow-y-auto p-8 border border-gray-200">
        {/* Title and Instructions */}
        <h2 className="text-2xl font-bold text-gray-900 mb-3">
          Reset Password
        </h2>
        <h3 className="text-base font-semibold text-gray-700 mb-2">
          Verify Your Email
        </h3>
        <p className="mb-6 text-sm text-gray-500">
          Enter the code sent to{" "}
          <span className="font-medium text-gray-700">{userName}</span> to reset
          your password.
        </p>

        {/* OTP Inputs */}
        <div className="flex justify-center my-6 space-x-3 bg-gray-50 p-4 rounded-lg border border-gray-200">
          {otp.map((value, index) => (
            <OtpInput
              key={index}
              value={value}
              index={index}
              onChange={handleInputChange}
              onBackspace={handleBackspace}
              className="w-12 h-12 text-center border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg font-medium text-gray-700 bg-white shadow-sm"
            />
          ))}
        </div>
        <div className="text-gray-600 mt-4 flex justify-between items-center">
          <span className="text-sm text-gray-500">
            Didn&apos;t receive the code?
          </span>
          <div className="flex items-center justify-center space-x-10">
            {isTimerActive && timer > 0 && (
              <span className="text-sm text-orange-600 font-semibold">
                Resend In {formatTimer(timer)}
              </span>
            )}
            <button
              onClick={handleResend}
              disabled={isTimerActive && timer > 0}
              className={`text-sm font-semibold transition-colors duration-200 ${
                isTimerActive && timer > 0
                  ? "text-gray-400 cursor-not-allowed opacity-50"
                  : "text-orange-600 hover:text-orange-500"
              }`}
            >
              Resend OTP
            </button>
          </div>
        </div>

        {/* Password Change Form */}
        <form className="mt-8" onSubmit={handleSubmit}>
          {/* New Password Field */}
          <label className="block text-sm font-medium text-gray-700 mb-2">
            New Password <span className="text-red-500 ml-1">*</span>
          </label>
          <div className="relative mb-6">
            <input
              type={showNewPassword ? "text" : "password"}
              value={newPassword}
              required
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700 shadow-sm"
            />
            <button
              type="button"
              onClick={handleToggleNewPassword}
              className="absolute inset-y-0 right-3 flex items-center text-gray-500"
            >
              {showNewPassword ? (
                <Eye className="h-5 w-5" />
              ) : (
                <EyeOff className="h-5 w-5" />
              )}
            </button>
          </div>

          {/* Confirm Password Field */}
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Confirm New Password <span className="text-red-500 ml-1">*</span>
          </label>
          <div className="relative mb-6">
            <input
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              required
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700 shadow-sm"
            />
            <button
              type="button"
              onClick={handleToggleConfirmPassword}
              className="absolute inset-y-0 right-3 flex items-center text-gray-500"
            >
              {showConfirmPassword ? (
                <Eye className="h-5 w-5" />
              ) : (
                <EyeOff className="h-5 w-5" />
              )}
            </button>
          </div>

          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            {[
              {
                label: "At least one lowercase letter",
                valid: validateNewPassword.lowercase,
              },
              {
                label: "Minimum 8 characters",
                valid: validateNewPassword.minLength,
              },
              {
                label: "Maximum 15 characters",
                valid: validateNewPassword.maxLength,
              },
              {
                label: "At least one uppercase letter",
                valid: validateNewPassword.uppercase,
              },
              {
                label: "At least one number",
                valid: validateNewPassword.number,
              },
              {
                label: "No more than 2 consecutive characters",
                valid: validateNewPassword.consecutive,
              },
              {
                label: "At least one special character",
                valid: validateNewPassword.specialChar,
              },
            ].map((rule, index) => (
              <div
                key={index}
                className={rule.valid ? "text-green-600" : "text-gray-500"}
              >
                <span
                  className={rule.valid ? "text-green-500" : "text-red-500"}
                >
                  {rule.valid ? "✔" : "✖"}
                </span>{" "}
                <span className="text-sm">{rule.label}</span>
              </div>
            ))}
          </div>
          {/* Confirm Password Match */}
          <div className="text-sm text-gray-500 mb-6">
            <span
              className={
                validateNewConfirmPassword.isSame
                  ? "text-green-600"
                  : "text-gray-500"
              }
            >
              <span
                className={
                  validateNewConfirmPassword.isSame
                    ? "text-green-500"
                    : "text-red-500"
                }
              >
                {validateNewConfirmPassword.isSame ? "✔" : "✖"}
              </span>{" "}
              Passwords match
            </span>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-5 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200 font-medium"
            >
              Cancel
            </button>
            <button
              disabled={!isValidNewPassword || !isValidConfirmNewPassword}
              type="submit"
              className={`px-5 py-2 rounded-lg text-white transition-colors duration-200 font-medium ${
                isValidNewPassword && isValidConfirmNewPassword
                  ? "bg-blue-600 hover:bg-blue-700"
                  : "bg-gray-400 cursor-not-allowed"
              }`}
            >
              Reset Password
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default PasswordExpiredModal;
